import { FC, useEffect, useMemo, useRef, useState, createContext, PropsWithChildren } from 'react';

import { Cookies } from '@hh.ru/magritte-common-cookies';
import { useResize } from '@hh.ru/magritte-common-resize';

import styles from './styles.less';

export enum Breakpoint {
    XS = 'xs',
    S = 's',
    M = 'm',
    L = 'l',
    XL = 'xl',
    XXL = 'xxl',
}

const DEFAULT_BREAKPOINT = Breakpoint.XS;

export const BreakpointContext = createContext<{ breakpoint: Breakpoint; renderRestriction: boolean }>({
    breakpoint: DEFAULT_BREAKPOINT,
    renderRestriction: false,
});

const detectBreakpointValue = (breakpointCheckerNode: HTMLDivElement) => {
    if (!('getComputedStyle' in window)) {
        return DEFAULT_BREAKPOINT;
    }

    const style = window.getComputedStyle(breakpointCheckerNode);

    if (!style) {
        return DEFAULT_BREAKPOINT;
    }

    const value = style.getPropertyValue('font-family').replace(/['"]/g, '');

    if (!value) {
        return DEFAULT_BREAKPOINT;
    }

    return Object.values(Breakpoint).find((size) => size === value) || DEFAULT_BREAKPOINT;
};

const TWO_WEEKS_IN_HOURS = 24 * 14;

interface BreakpointProviderProps extends PropsWithChildren {
    /** Флаг, который позволяет сохранять значение брекпоинта в куку */
    storeActualBreakpointValueInCookie?: boolean;
    /** Cохраненное значение брекпоинта, которое будет использоваться начальным */
    storedBreakpointValue?: Breakpoint;
    /** Флаг, который позволяет использовать только сохраненное значение брекпоинта
     * работает если есть сохраненное значение */
    force?: boolean;
    /** Флаг, который включает ограничение рендера, позволяет отключить SSR
     * для колонок, значение брекпоинта которых не совпадает с значением в контексте */
    renderRestriction?: boolean;
}

const BreakpointProvider: FC<BreakpointProviderProps> = ({
    children,
    storeActualBreakpointValueInCookie = false,
    storedBreakpointValue,
    force = false,
    renderRestriction = false,
}) => {
    const breakpointCheckerNodeRef = useRef<HTMLDivElement>(null);
    const [breakpoint, setBreakpoint] = useState(storedBreakpointValue ?? DEFAULT_BREAKPOINT);
    const useOnlyStoredValue = !!(force && storedBreakpointValue);

    const contextValue = useMemo(
        () => ({
            breakpoint: useOnlyStoredValue ? storedBreakpointValue : breakpoint,
            renderRestriction,
        }),
        [breakpoint, renderRestriction, useOnlyStoredValue, storedBreakpointValue]
    );

    const sideEffect = () => {
        if (!useOnlyStoredValue && breakpointCheckerNodeRef.current) {
            const breakpoint = detectBreakpointValue(breakpointCheckerNodeRef.current);
            setBreakpoint(breakpoint);

            if (storeActualBreakpointValueInCookie) {
                Cookies.set('device_magritte_breakpoint', breakpoint, TWO_WEEKS_IN_HOURS);
            }
        }
    };

    useEffect(sideEffect, [useOnlyStoredValue, storeActualBreakpointValueInCookie]);
    useResize(sideEffect);

    return (
        <BreakpointContext.Provider value={contextValue}>
            {children}
            <div ref={breakpointCheckerNodeRef} className={styles['breakpoint-checker']} />
        </BreakpointContext.Provider>
    );
};

BreakpointProvider.displayName = 'BreakpointProvider';

export default BreakpointProvider;
